import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  notification,
  Radio,
  Row,
  TableProps,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IProduct, IUpdateProductAllocation } from '@/interfaces/product';
import { addProduct } from '@/adapters/product';
import UnitSelector from '@/components/selector/unit-selector';
import { IInboundItem } from '@/interfaces/inbound';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import TableComponent from '@/components/table';
import { debounce, sumBy } from 'lodash';
import { AxiosResponse } from 'axios';
import { IBusinessUnit } from '@/interfaces/business-unit';
import { queryBusinessUnits } from '@/adapters/business-unit';
import Title from '@/components/title';

interface IProps {
  handleOk: (product: IProduct) => void;
  data?: Partial<IInboundItem>;
}

const InboundProductCreate = ({ handleOk, data }: IProps) => {
  const [form] = Form.useForm<IProduct>();

  const [productAllocations, setProductAllocations] = useState<
    IUpdateProductAllocation[]
  >([]);

  const { data: bus } = useQuery<AxiosResponse<IBusinessUnit[]>, Error>({
    queryKey: ['search-bu'],
    queryFn: async () => await queryBusinessUnits(),
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IProduct>) => await addProduct(data),
    mutationKey: ['actionProduct'],
    onSuccess: (data, req) => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = 'สร้างสินค้าสำเร็จ';
        notification.success({
          message: message,
        });
        form.resetFields();
        handleOk({
          ...(data.data as IProduct),
          isSendInbound: req.isSendInbound,
        });
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IProduct>) => {
        if (values.type !== 'product') values.code = 'sample';
        if (values.type == 'sample') {
          values.iSample = true;
          values.unitPrice = Number(data?.totalCost || 0);
        }
        if (values.type == 'equipment') {
          values.iSample = true;
          values.isEquipment = true;
          values.unitPrice = Number(data?.totalCost || 0);
        }
        values.remark = values.forCustomer;
        values.productAllocations = productAllocations;
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
    setProductAllocations([]);
  };

  const fullPercentage = sumBy(productAllocations, r => r.percentage);
  const isFullPercentage = fullPercentage == 100;

  const debouncedSearch = useCallback(
    debounce((rec, value: number) => {
      setProductAllocations(prev =>
        prev.map(item =>
          item.businessUnitId == rec.businessUnitId
            ? {
                ...item,
                percentage:
                  fullPercentage + Number(value) > 100
                    ? 100 - fullPercentage
                    : Number(value),
              }
            : item,
        ),
      );
    }, 300),
    [],
  );

  const columns: TableProps<IUpdateProductAllocation>['columns'] = [
    {
      title: 'BU',
      dataIndex: 'bu',
      key: 'bu',
      className: 'font-bold',
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 200,
      render: (_, rec) => (
        <InputNumber
          type="number"
          placeholder="Percentage"
          className="w-full"
          value={rec.percentage}
          disabled={isFullPercentage && !rec.percentage}
          min={0}
          max={100}
          onChange={v => debouncedSearch(rec, Number(v))}
        />
      ),
    },
  ];

  const packageUnitId = Form.useWatch('packageUnitId', form);
  const baseUnitId = Form.useWatch('baseUnitId', form);
  const productType = Form.useWatch('type', form) || '';

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        code: data.productCode,
        name: data.pdtDescriptionE,
      });
    }
  }, [data]);

  useEffect(() => {
    if (bus?.data) {
      const allos = bus?.data?.map(b => {
        const allo: IUpdateProductAllocation = {
          businessUnitId: b.id,
          isActive: true,
          percentage: 0,
          productId: 0,
          bu: b.name,
        } as IUpdateProductAllocation;
        return allo;
      });
      setProductAllocations(allos);
      form.setFieldsValue({
        isSendInbound: productType == 'product',
      });
    }
    if (['sample', 'equipment'].includes(productType)) {
      form.setFieldsValue({
        baseUnitId: 'EA',
        packageUnitId: 'EA',
        unitPrice: 0,
      });
    } else {
      form.setFieldsValue({
        baseUnitId: undefined,
        packageUnitId: undefined,
        unitPrice: undefined,
      });
    }
    return () => {
      setProductAllocations([]); // Reset state when component unmounts
    };
  }, [bus?.data, productType]);

  return (
    <div className="flex w-full mb-2">
      <Form
        form={form}
        name="product"
        onFinish={handleUpdate}
        layout="vertical"
        className="w-full"
      >
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item<IProduct>
              label="ประเภท"
              name="type"
              rules={[{ required: true, message: 'เลือกประเภท' }]}
            >
              <Radio.Group>
                <Radio value={'product'}>สินค้า</Radio>
                <Radio value={'sample'}>ตัวอย่าง</Radio>
                <Radio value={'equipment'}>อุปกรณ์</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          {productType && (
            <>
              <Col span={24} md={8}>
                <Form.Item
                  label="รหัสสินค้า"
                  name="code"
                  rules={[
                    {
                      required: !['sample', 'equipment'].includes(productType),
                      message: 'ระบุรหัสสินค้า',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    disabled={['sample', 'equipment'].includes(productType)}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={16}>
                <Form.Item
                  label="ชื่อสินค้า"
                  name="name"
                  rules={[{ required: true, message: 'ระบุชื่อสินค้า' }]}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
              <Col span={24} md={8}>
                <Form.Item
                  label="หน่วย Package"
                  name="packageUnitId"
                  rules={[{ required: true, message: 'เลือกหน่วย Package' }]}
                >
                  <UnitSelector
                    value={packageUnitId}
                    size="large"
                    handleSelectData={e =>
                      form.setFieldValue('packageUnitId', e)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={8}>
                <Form.Item
                  label="Conversion"
                  name="conversion"
                  rules={[{ required: true, message: 'ระบุConversion' }]}
                  initialValue={1}
                >
                  <InputNumber size="large" className="w-full" min={1} />
                </Form.Item>
              </Col>
              <Col span={24} md={8}>
                <Form.Item
                  label="หน่วยขาย"
                  name="baseUnitId"
                  rules={[{ required: true, message: 'เลือกหน่วยขาย' }]}
                >
                  <UnitSelector
                    value={baseUnitId}
                    size="large"
                    handleSelectData={e => form.setFieldValue('baseUnitId', e)}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={8}>
                <Form.Item
                  label="ราคาขาย/หน่วย"
                  name="unitPrice"
                  rules={[{ required: true, message: 'ระบุราคาขาย' }]}
                >
                  <InputNumber size="large" className="w-full" />
                </Form.Item>
              </Col>
              {productType !== 'product' && (
                <>
                  <Col span={24} md={8}>
                    <Form.Item<IProduct>
                      label="BU"
                      name="businessUnitId"
                      rules={[
                        {
                          required: ['sample', 'equipment'].includes(
                            productType,
                          ),
                          message: 'ระบุ BU',
                        },
                      ]}
                    >
                      <BusinessUnitSelector
                        handleSelectData={id => {
                          form.setFieldsValue({
                            businessUnitId: id,
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={8}>
                    <Form.Item
                      label="ใช้สำหรับ(ชื่อลูกค้า)"
                      name="forCustomer"
                      rules={[
                        {
                          required: ['sample', 'equipment'].includes(
                            productType,
                          ),
                          message: 'ระบุข้อมูล',
                        },
                      ]}
                    >
                      <Input size="large" className="w-full" />
                    </Form.Item>
                  </Col>
                </>
              )}

              <Col span={24}>
                <Form.Item label="หมายเหตุ" name="remark">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        {productType && (
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item<IProduct>
                label="สินค้าเข้าคลังหรือไม่"
                name="isSendInbound"
                rules={[{ required: true, message: 'สินค้าเข้าคลังหรือไม่' }]}
                initialValue={undefined}
              >
                <Radio.Group>
                  <Radio value={true}>นำเข้า</Radio>
                  <Radio value={false}>ไม่นำเข้า</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        )}
        {productType == 'product' && (
          <>
            <Title
              customClassName="text-lg text-blue-700 mb-4"
              customContainerClassName="py-0"
              title={`Cost Allocations`}
            />
            <Row gutter={12}>
              <Col span={24} className="mb-4">
                <TableComponent<IUpdateProductAllocation>
                  columns={columns}
                  criteria={undefined}
                  data={{ data: productAllocations || [], totalRecords: 0 }}
                  loading={false}
                  onTableChange={undefined}
                  showPagination={false}
                  scroll={false}
                  rowKey="businessUnitId"
                />
              </Col>
            </Row>
          </>
        )}
        <Row gutter={12}>
          <Col span={24} className="flex flex-row gap-2 justify-end">
            <Button key="back" onClick={onReset}>
              ยกเลิก
            </Button>
            <Button
              key="submit"
              type="primary"
              form="productGroup"
              loading={mutations.isPending}
              disabled={fullPercentage != 100 && productType == 'product'}
              onClick={handleUpdate}
            >
              บันทึก
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default InboundProductCreate;
